import React from 'react';
import PropTypes from 'prop-types';
import { Switch, Route, Redirect } from 'react-router-dom';
import CloudFolder from 'containers/CloudFolder/CloudFolder';
import Users from 'containers/Users/Users';
import NotFound from 'containers/NotFound/NotFound';
import NoPermissions from 'containers/NoPermissions/NoPermissions';
import PageNotAvailable from 'containers/PageNotAvailable/PageNotAvailable';
import SubscriptionSuccessful from 'containers/SubscriptionSuccessful/SubscriptionSuccessful';
import CompanyProfile from 'containers/CompanyProfile/CompanyProfile';
import Subscription from 'containers/Subscription/Subscription';
import Showcase from 'containers/Showcase/Showcase';
import OnBoarding from 'containers/OnBoarding/OnBoarding';
import Fonts from 'containers/Fonts/Fonts';
import SmartFields from 'containers/SmartFields/SmartFields';
import XCPlus from 'containers/XCPlus/XCPlus';
import AccountDetails from 'containers/AccountDetails/AccountDetails';
import Customize from 'containers/Customize/Customize';
import { SUBSCRIPTION_PLAN, getCloudPlans } from '../../config';
import WrongAccount from 'containers/WrongAccount/WrongAccount';
import { parseQuery } from '../../utils/url';
import {
	getRoute,
	ROOT,
	CLOUD_FOLDER,
	SUBSCRIPTION,
	SUBSCRIPTION_PLUS_OVERVIEW,
	SUBSCRIPTION_PERPETUAL_OVERVIEW,
	SUBSCRIPTION_UPGRADE,
	USERS,
	FONTS,
	SMART_FIELDS,
	NOT_FOUND,
	UI,
	ONBOARDING,
	CLOUD_FOLDER_TAB,
	CLOUD_FOLDER_TAB_ASSET,
	NO_PERMISSIONS,
	PAGE_NOT_AVAILABLE,
	XC_PLUS,
	SUBSCRIPTION_SUCCESSFUL,
	WRONG_ACCOUNT,
	ACCOUNT_DETAILS,
	CUSTOMIZE
} from 'routes';

const MainContent = props => {

	const {
		onBoarding,
		isAdmin,
		isTempCompany,
		plan,
		isCompanyFoundInSession,
		selectedId,
		isDesktopApp,
		isCBPlanExternalRedirect,
		isCBPlanInternalRedirect,
		isPerpetualOwner,
		navOpen,
		couponId,
		isDiscountTakeoverFromWebsite,
		productName,
		productSource,
		productPeriod,
		productQuantity,
		magixCat,
		isUpgradePopup,
		subscriptionId,
		reactivateSubscription
	} = props;

	const renderRoute = (Component, adminOnly, extraProps = {}) => {
		
		const RoutingComponent = props => {
			if (onBoarding) {
				return <Redirect to={ONBOARDING} />;
			}

			const { match: { path, params: { currency, planId } } } = props;

			// First we check if company is in session, before using isAdmin and isTempCompany as these are
			// resolved based on companies.selectedId which might be from other account
			if (!isCompanyFoundInSession) {
				const queryParams = parseQuery(props.location.search);
				const queryParamsCurrency = queryParams['currency'];
				const queryParamsProductName = queryParams['prod'];
				const queryParamsProductSource = queryParams['source'];
				const queryParamsProductPeriod = queryParams['period'];
				const queryParamsProductQuantity = productQuantity;
				const queryParamsProductLanguage = queryParams['lang'];
				const queryParamsMagixSerial = queryParams['pserial'];
				const queryParamsMagixEmail = queryParams['pemail'];
				const queryParamsMagixCat = queryParams['pcat'];

				if (queryParams['currency']) {
					delete queryParams['currency'];
				}

				return (<Redirect
					to={{
						pathname: getRoute(WRONG_ACCOUNT, selectedId),
						state: { 
							prevLocationPath: path, 
							prevLocationSearch: queryParams,
							prevLocationCurrency: currency || queryParamsCurrency,
							prevLocationProductName: queryParamsProductName,
							prevLocationProductSource: queryParamsProductSource,
							prevLocationProductPeriod: queryParamsProductPeriod,
							prevLocationProductQuantity: queryParamsProductQuantity,
							prevLocationProductLanguage: queryParamsProductLanguage,
							prevLocationMagixSerial: queryParamsMagixSerial,
							prevLocationMagixEmail: queryParamsMagixEmail,
							prevLocationMagixCat: queryParamsMagixCat,
							prevLocationCouponId: couponId,
							prevLocationPlanId: planId,
							prevLocationUpgradePopup: isUpgradePopup,
							prevLocationIsCBPlanExternalRedirect: isCBPlanExternalRedirect,
							prevLocationIsCBPlanInternalRedirect: isCBPlanInternalRedirect,
							isDesktopApp: isDesktopApp,
						}
					}}
				/>);
			}

			// Show No Permission screen if
			// Route is not available for not admins
			// OR Route is not available for companies with a temporary name ([P9052-15208] PDF simplified workflow)
			// Always show Team profile and Users and permissions tab for Team/Team+ [P9052-20321]
			if (
				(adminOnly && !isAdmin)
				|| (isDesktopApp && extraProps.hideForDesktop === true)
				|| (isTempCompany && extraProps.isVisibleForTempTeams !== true &&
					!(extraProps.isVisibleForDesktopTempTeams && isDesktopApp) &&
					!(extraProps.showForPerpetualOwners && isPerpetualOwner) &&
					(!extraProps.showForTempCompanyOnPlan || !extraProps.showForTempCompanyOnPlan.includes(plan)))
			) {
				if (adminOnly && !isAdmin) {
					return <Redirect to={NO_PERMISSIONS} />;
				} else {
					return (<Redirect 
						to={getRoute(PAGE_NOT_AVAILABLE, selectedId)}
					/>);		
				}
			}

			// Add flag to show "complete team profile" modal if user has temporary company and is trying to see user component
			if (extraProps.checkForCompletedCompany && isTempCompany && extraProps.isVisibleForTempTeams !== true) {
				extraProps.askToCompleteCompanyProfile = true;
			}

			return <Component {...props} {...extraProps} />;
		};

		RoutingComponent.displayName = 'RoutingComponent';

		return RoutingComponent;
	};

	return (
		<Switch>
			<Route path={UI} component={Showcase} />
			<Route path={NOT_FOUND} component={NotFound} />
			<Route path={WRONG_ACCOUNT} component={WrongAccount} />
			<Route path={NO_PERMISSIONS} component={NoPermissions} />
			<Route path={PAGE_NOT_AVAILABLE} component={PageNotAvailable} />
			<Route path={ONBOARDING} component={OnBoarding} />
			<Route path={CLOUD_FOLDER_TAB_ASSET} render={renderRoute(CloudFolder, true)} />
			<Route path={CLOUD_FOLDER_TAB} render={renderRoute(CloudFolder, true)} />
			<Route
				path={CLOUD_FOLDER}
				render={
					renderRoute(
						CloudFolder,
						true,
						{ hideForDesktop: true, showForTempCompanyOnPlan: getCloudPlans()}
					)
				} />
			<Route
				path={SUBSCRIPTION_PLUS_OVERVIEW}
				render={
					renderRoute(
						Subscription,
						false,
						{
							plusOverview: true,
							isVisibleForTempTeams: true,
							isDiscountTakeoverFromWebsite,
							isCBPlanInternalRedirect,
							productName,
							productSource,
							productPeriod,
							productQuantity,
							magixCat,
							couponId,
							navOpen
						}
					)
				} />
			<Route
				path={SUBSCRIPTION_PERPETUAL_OVERVIEW}
				render={
					renderRoute(
						Subscription,
						false,
						{
							perpetualOverview: true,
							productName,
							productSource,
							productQuantity,
							isVisibleForTempTeams: true,
							isCBPlanInternalRedirect,
							navOpen,
							couponId
						}
					)
				} />
			<Route
				path={SUBSCRIPTION_UPGRADE}
				render={
					renderRoute(
					Subscription,
					true,
					{
						isVisibleForTempTeams: true,
						productName,
						productSource,
						productPeriod,
						productQuantity,
						couponId,
						isCBPlanExternalRedirect,
						isCBPlanInternalRedirect,
						navOpen
					})
				} />
			<Route
				path={SUBSCRIPTION}
				render={
					renderRoute(
					Subscription,
					true,
					{ 
						isCBPlanExternalRedirect,
						isCBPlanInternalRedirect,
						navOpen,
						isVisibleForTempTeams: true,
						productName,
						productSource,
						productPeriod,
						productQuantity,
						magixCat,
						couponId,
						reactivateSubscription, 
						isStandardProductOverview: true }
					)
				} />
			<Route
				path={SUBSCRIPTION_SUCCESSFUL}
				render={
					renderRoute(
					SubscriptionSuccessful,
					true,
					{ isVisibleForTempTeams: true, subscriptionId }
					)
				} />
			<Route
				path={USERS}
				render={
					renderRoute(
						Users,
						true,
						{
							checkForCompletedCompany: true,
							showForTempCompanyOnPlan: [
								SUBSCRIPTION_PLAN.TEAM_PLUS,
								SUBSCRIPTION_PLAN.ESSENTIAL,
								SUBSCRIPTION_PLAN.WEB,
								SUBSCRIPTION_PLAN.DESIGNER,
								SUBSCRIPTION_PLAN.PHOTO								
							],
							isVisibleForDesktopTempTeams: true,
							showForPerpetualOwners: true
						}
					)
				} />
			<Route
				path={FONTS}
				render={
					renderRoute(
						Fonts,
						true, 
						{ hideForDesktop: true, showForTempCompanyOnPlan: getCloudPlans()}
					)
				} />
			<Route
				path={SMART_FIELDS}
				render={
					renderRoute(
						SmartFields,
						true,
						{ hideForDesktop: true, showForTempCompanyOnPlan: getCloudPlans()}
					)
				} />
			<Route path={XC_PLUS} render={renderRoute(XCPlus, false, { isVisibleForTempTeams: true })} />
			<Route path={ACCOUNT_DETAILS} render={renderRoute(AccountDetails, false, { isVisibleForTempTeams: true })} />
			<Route
				path={CUSTOMIZE}
				render={
					renderRoute(
						Customize,
						false,
						{ isVisibleForTempTeams: false, hideForDesktop: true, showForTempCompanyOnPlan: getCloudPlans()}
					)
				} />
			<Route
				path={ROOT}
				exact
				render={
					renderRoute(
						CompanyProfile,
						false,
						{
							showForTempCompanyOnPlan: [
								SUBSCRIPTION_PLAN.TEAM_PLUS,
								SUBSCRIPTION_PLAN.ESSENTIAL,
								SUBSCRIPTION_PLAN.WEB,
								SUBSCRIPTION_PLAN.DESIGNER,
								SUBSCRIPTION_PLAN.PHOTO								
							],
							isVisibleForDesktopTempTeams: true,
							showForPerpetualOwners: true
						}
					)
				} />
			<Route path="/" exact render={renderRoute(CompanyProfile)} />
			<Redirect to={NOT_FOUND} />
		</Switch>
	);
};

MainContent.propTypes = {
	onBoarding: PropTypes.bool,
	isAdmin: PropTypes.bool,
	isTempCompany: PropTypes.bool,
	match: PropTypes.object,
	plan: PropTypes.string,
	selectedId: PropTypes.string,
	isCompanyFoundInSession: PropTypes.bool,
	isDesktopApp: PropTypes.bool,
	couponId: PropTypes.string,
	isDiscountTakeoverFromWebsite: PropTypes.bool,
	isCBPlanExternalRedirect: PropTypes.bool,
	isCBPlanInternalRedirect: PropTypes.bool,
	magixCat: PropTypes.string,
	isUpgradePopup: PropTypes.bool,
	reactivateSubscription: PropTypes.bool,
	navOpen: PropTypes.bool,
	isPerpetualOwner: PropTypes.bool,
	productName: PropTypes.string,
	currency: PropTypes.string,
	productSource: PropTypes.string,
	productPeriod: PropTypes.string,
	productQuantity: PropTypes.string,
	subscriptionId: PropTypes.string,
	location: PropTypes.shape({
		search: PropTypes.string
	})
};

export default MainContent;


// WEBPACK FOOTER //
// ./src/components/MainContent/MainContent.js